import { LockClosedIcon } from "@radix-ui/react-icons";
import { MessageResponse } from "client/openapi";
import moment from "moment";
import { useContext } from "react";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import { isEmail, isInbound } from "./conversations";
import FromToLine from "./FromToLine";
import { privateNote } from "./privacy";
import ReplyLink from "./ReplyLink";

/**
 * One message of a thread: received on the left in green, sent on the right
 * in blue, with who sent and received it on one gray line. An email also
 * offers Reply (answer this one), and is outlined while it is the email
 * being answered. Mail kept to some admins (their personal addresses') says
 * who sees it.
 */
export default function MessageBubble({
  message,
  organizationName,
  onReply,
  answering = false,
  onUnknownSender,
}: {
  message: MessageResponse;
  /** A received message's first recipient on the From and To line. */
  organizationName: string;
  /** Answer this email instead of the newest. */
  onReply?: () => void;
  /** This is the email being answered. */
  answering?: boolean;
  /** Link or add a sender who matches no one. */
  onUnknownSender?: () => void;
}) {
  const inbound = isInbound(message);
  const email = isEmail(message);
  const { account } = useContext(OrgRolesAndAccountContext);
  const onlyFor = privateNote(message, account?.reference_id);
  return (
    <div className={`flex ${inbound ? "justify-start" : "justify-end"}`}>
      <div
        className={`max-w-[70%] p-3 rounded-lg ${
          inbound ? "bg-green-100" : "bg-blue-100"
        } ${answering ? "ring-2 ring-[#2b77cf] ring-offset-2" : ""}`}
      >
        <div className="mb-1 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-xs text-gray-500">
              {moment(message.created_at).format("MMM Do, h:mm a")}
            </div>
            <FromToLine
              message={message}
              organizationName={organizationName}
              onUnknownSender={onUnknownSender}
            />
            {onlyFor && (
              <div className="mt-0.5 flex items-center gap-1 text-xs font-medium text-gray-600">
                <LockClosedIcon aria-hidden />
                {onlyFor}
              </div>
            )}
          </div>
          {email && onReply && (
            <ReplyLink onClick={onReply} active={answering} />
          )}
        </div>
        {message.subject && (
          <div className="font-semibold mb-1">{message.subject}</div>
        )}
        <div
          style={{
            wordWrap: "break-word",
            overflowWrap: "break-word",
            maxWidth: "700px",
          }}
        >
          {message.content}
        </div>
      </div>
    </div>
  );
}
